/**
 * Das Gerüst der Apartmentakte.
 *
 * Von oben nach unten: Meta-Nav-Strip mit Rückweg, Apartmentwechsel und Aktionen
 * (`ApartmentNavStrip`), Name mit Zustand und Kennzeile, Reiterleiste (`ApartmentTabBar`) und
 * darunter der jeweilige Reiter. Die Mutationen liegen in `useApartmentActions`.
 */
import { Link, Outlet, useNavigate, useParams, useRouterState } from '@tanstack/react-router';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { channelIsActive, useApi } from '@/shared/api';
import { isWorkspaceAdmin, useAuth } from '@/shared/auth';
import { propertyKeys } from '../queries';
import { apartmentActionMenu } from './apartmentActionMenu';
import { ApartmentDetailDialogs } from './ApartmentDetailDialogs';
import { ApartmentDetailContext } from './ApartmentDetailContext';
import { ApartmentIdentity } from './ApartmentIdentity';
import { ApartmentNavStrip } from './ApartmentNavStrip';
import { ApartmentTabBar } from './ApartmentTabBar';
import { useApartmentActions } from './useApartmentActions';
import { ArchivedPropertyNotice } from './ArchivedPropertyNotice';
import { apartmentLifecycleUi } from './apartmentLifecycleUi';
import { activeApartmentTab, visibleApartmentTabs } from './apartmentTabs';

export { useApartmentDetail } from './ApartmentDetailContext';

export function ApartmentDetailLayout() {
  const { id } = useParams({ from: '/_admin/properties/$id' });
  const api = useApi();
  const navigate = useNavigate();
  const routerState = useRouterState();
  const currentPath = routerState.location.pathname;
  const { user } = useAuth();

  const [archiveOpen, setArchiveOpen] = useState(false);
  // H3-A2: das Badge „Pausiert" stand hier schon, die Aktion lag nur am Objekt. Derselbe Dialog
  // wie in der Veroeffentlichungszentrale — zwei Implementierungen waeren zwei Haertungen.
  const [lifecycleOpen, setLifecycleOpen] = useState(false);
  const [manualBookingOpen, setManualBookingOpen] = useState(false);

  const { data: property, isLoading } = useQuery({
    queryKey: propertyKeys.detail(id),
    queryFn: () => api.properties.get(id),
  });

  const { preflight, archive, lifecycleMutation } = useApartmentActions(id, archiveOpen, () =>
    setArchiveOpen(false),
  );

  const { data: channelsData } = useQuery({
    queryKey: ['channex', 'channels'],
    queryFn: () => api.channex.listChannels(),
    staleTime: 60_000,
    // Channex-Channel-Liste ist WorkspaceAdmin-only; sonst 403 auf der Owner/Reinigung-Landing.
    enabled: !!property?.channexUnitPublished && isWorkspaceAdmin(user),
  });
  const mappedChannels =
    channelsData?.data.filter((c) =>
      c.attributes.properties?.includes(property?.channexPropertyId ?? ''),
    ) ?? [];
  const activeChannelCount = mappedChannels.filter(channelIsActive).length;

  if (isLoading || !property) {
    return <p className="text-ink-soft p-8">Lade Apartment…</p>;
  }

  const lifecycle = apartmentLifecycleUi(user, property.status);
  const visibleTabs = visibleApartmentTabs(user, lifecycle.readOnly);
  // Der Reiter, auf dem der Nutzer steht — F4 springt damit auf denselben Reiter des Nachbarn.
  const activeTab = activeApartmentTab(visibleTabs, id, currentPath);
  const currentViewAvailable = activeTab !== undefined;
  const actions = apartmentActionMenu(lifecycle, {
    onEditMasterData: () => void navigate({ to: '/properties/$id/stammdaten', params: { id } }),
    onLifecycle: () => setLifecycleOpen(true),
    onArchive: () => setArchiveOpen(true),
  });

  return (
    <div>
      <ApartmentNavStrip
        propertyId={id}
        activeTabTo={activeTab?.to ?? '/properties/$id'}
        canEdit={lifecycle.canEdit}
        showManualBooking={!!property.channexPropertyId && property.status === 'Active'}
        onManualBooking={() => setManualBookingOpen(true)}
        actions={actions}
      />

      <ApartmentIdentity
        property={property}
        channelsActive={activeChannelCount}
        channelsTotal={mappedChannels.length}
      />

      <ApartmentTabBar
        propertyId={id}
        tabs={visibleTabs}
        activeTo={activeTab?.to}
        currentPath={currentPath}
      />

      {lifecycle.readOnly && <ArchivedPropertyNotice />}

      <ApartmentDetailContext.Provider
        value={{
          propertyId: id,
          propertyGroupId: property.propertyGroupId ?? null,
          currency: property.currency,
          readOnly: lifecycle.readOnly,
        }}
      >
        {currentViewAvailable ? (
          <Outlet />
        ) : (
          <div className="border-line bg-card rounded-lg border p-6">
            <p className="text-ink font-serif text-xl">Bereich im Archiv geschlossen</p>
            <p className="text-ink-soft mt-2 text-sm">
              Operative Bearbeitung ist für archivierte Apartments deaktiviert.
            </p>
            <Link
              to="/properties/$id"
              params={{ id }}
              search={(prev) => prev}
              className="text-sage mt-4 inline-block text-sm font-semibold hover:underline"
            >
              Zur Archivübersicht
            </Link>
          </div>
        )}
      </ApartmentDetailContext.Provider>

      <ApartmentDetailDialogs
        property={property}
        readOnly={lifecycle.readOnly}
        lifecycleAction={lifecycle.lifecycleAction}
        manualBookingOpen={manualBookingOpen}
        onManualBookingChange={setManualBookingOpen}
        lifecycleOpen={lifecycleOpen}
        onLifecycleChange={setLifecycleOpen}
        lifecycleMutation={lifecycleMutation}
        archiveOpen={archiveOpen}
        onArchiveChange={setArchiveOpen}
        preflight={preflight}
        archive={archive}
      />
    </div>
  );
}
